import React from 'react';
import {
  Box,
  Button,
  Flex,
  MultiCombobox,
  Text,
  useDisclosure,
  Badge,
  Icon,
  AbstractButton,
  BadgeVariant,
} from 'pounceV2';
import { useTheme } from '@mui/material';
import LimitItemDisplayV3 from 'Components/LimitItemDisplay/LimitItemDisplayV3';
import AdditionalBadgesJewelV3 from 'Components/AdditionalBadgesJewel/AdditionalBadgesJewelV3';
import {
  INDICATOR_OPTIONS,
  INDICATOR_OPTIONS_MAP,
  IndicatorOption,
  indicatorToString,
  isIndicatorEligible,
  getIndicatorLabel,
} from './indicatorOptions';

export interface SchemaFieldShape {
  type: string;
  name?: string;
  required?: boolean;
  description?: string;
  timeFormats?: string[];
  indicators?: string[];
  element?: SchemaFieldShape; // used in arrays
  fields?: SchemaFieldShape[]; // used in objects
  isDiscovered?: boolean;
}

export interface SchemaFieldProps {
  prefix?: string;
  initialExpansionLevel?: number;
  field: SchemaFieldShape;
  onIndicatorsChange?: (fieldPath: string, indicators: string[]) => void;
}

const getBadgeVariant = (type: string): BadgeVariant => {
  switch (type) {
    case 'object':
      return 'accent-outline';
    case 'array':
      return 'neutral-outline';
    default:
      return 'info-outline';
  }
};

const IndicatorList: React.FC<{ indicators: string[]; color: string }> = ({
  indicators,
  color,
}) => (
  <Flex align="center" gap={1} mx={1}>
    <Text as="span">Indicators:</Text>
    <LimitItemDisplayV3 limit={3} AdditionalItemsComponent={AdditionalBadgesJewelV3}>
      {indicators.map((value, index) => (
        <Text key={value} as="span" color={color}>
          {getIndicatorLabel(value)}
          {index < indicators.length - 1 ? ',' : ''}
        </Text>
      ))}
    </LimitItemDisplayV3>
  </Flex>
);

const SchemaFieldV3: React.FC<SchemaFieldProps> = ({
  prefix,
  field,
  initialExpansionLevel = 0,
  onIndicatorsChange,
}) => {
  const theme = useTheme();
  const { isOpen, toggle } = useDisclosure({ initialState: initialExpansionLevel > 0 });
  const [isComboboxOpen, setIsComboboxOpen] = React.useState(false);

  // Compute the full field path for the callback
  const fieldPath = prefix ? `${prefix}${field.name ? `.${field.name}` : ''}` : field.name || '';

  // Whether this field supports indicators
  const canHaveIndicators = !!onIndicatorsChange && isIndicatorEligible(field.type);
  const hasIndicators = field.indicators?.length > 0;

  // Convert current indicator string values to IndicatorOption objects
  const selectedIndicators = React.useMemo(
    () =>
      (field.indicators || [])
        .map(value => INDICATOR_OPTIONS_MAP.get(value))
        .filter((o): o is IndicatorOption => o != null),
    [field.indicators],
  );

  const handleIndicatorChange = React.useCallback(
    (items: IndicatorOption[]) => {
      onIndicatorsChange?.(
        fieldPath,
        items.map(i => i.value),
      );
    },
    [onIndicatorsChange, fieldPath],
  );

  const handleDeleteIndicators = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onIndicatorsChange?.(fieldPath, []);
    },
    [onIndicatorsChange, fieldPath],
  );

  const openCombobox = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsComboboxOpen(true);
  }, []);

  const closeCombobox = React.useCallback(() => {
    setIsComboboxOpen(false);
  }, []);

  const isObject = Boolean(field.fields);
  const isArray = Boolean(field.element);
  const showNestedFields = isObject && isOpen;
  const headerProps = isObject
    ? {
        as: AbstractButton,
        onClick: toggle,
        _hover: {
          backgroundColor: theme.palette.tokens.background.brand.blue[5],
          borderRadius: '8px',
        },
        'aria-label': `Toggle ${field.name}`,
        display: 'block',
      }
    : {};

  return (
    <>
      <Box p={0} mx={2} {...headerProps}>
        <Flex align="center" gap={1} position="relative" minHeight={48}>
          <Box width={20}>
            {isObject && (
              <Icon
                transition="transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 80ms"
                transform={isOpen ? 'rotate(90deg)' : 'rotate(0deg)'}
                type="chevron-right"
                size="small"
              />
            )}
          </Box>
          <Flex align="center" fontWeight="medium" gap={1}>
            <Text as="span" color={theme.palette.tokens.content.text.neutral[2]} variant="body/lg">
              {prefix && (
                <Text as="span" color={theme.palette.tokens.content.text.neutral[2]}>
                  {prefix}
                  {field.name ? '.' : ''}
                </Text>
              )}
              <Text as="span" variant="body/lg" mr={1}>
                {field.name}
              </Text>
            </Text>
            {field.required && (
              <Text as="span" color={theme.palette.tokens.content.text.semantic.error.base} mr={1}>
                *
              </Text>
            )}
            <Badge borderStyle="pill" variant={getBadgeVariant(field.type)} py={1} px={2}>
              {field.type === 'array' ? `${field.type}: ${field.element.type}` : field.type}
            </Badge>
            {isObject && (
              <Badge
                borderStyle="pill"
                variant={showNestedFields ? 'primary-outline' : 'primary-solid'}
                py={1}
                px={2}
              >
                {field.fields.length} Nested Fields
              </Badge>
            )}
            {/* Indicator editing UI — only for eligible types when callback provided */}
            {canHaveIndicators && !hasIndicators && !isComboboxOpen && (
              <Button ml={2} variant="secondary" size="small" onClick={openCombobox}>
                Add Indicators
              </Button>
            )}

            {canHaveIndicators && isComboboxOpen && (
              <Flex align="center" gap={1} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Text as="span" mx={1}>
                  Indicators:
                </Text>
                <Box width={320}>
                  <MultiCombobox
                    label="Indicators"
                    hideLabel
                    autoFocus
                    openOnFocus
                    items={INDICATOR_OPTIONS}
                    itemToString={indicatorToString}
                    value={selectedIndicators}
                    onChange={handleIndicatorChange}
                    placeholder="Select indicators..."
                    searchable
                    maxContainerHeight={80}
                    onClose={closeCombobox}
                    renderContent={({ value: items }) => (
                      <Flex gap={1}>
                        <LimitItemDisplayV3 limit={2}>
                          {items.map(item => (
                            <Box key={indicatorToString(item)} pr={1}>
                              {indicatorToString(item)}
                            </Box>
                          ))}
                        </LimitItemDisplayV3>
                      </Flex>
                    )}
                  />
                </Box>
              </Flex>
            )}

            {canHaveIndicators && hasIndicators && !isComboboxOpen && (
              <Flex align="center" gap={1}>
                <IndicatorList
                  indicators={field.indicators}
                  color={theme.palette.tokens.content.text.accent[3].base}
                />
                <Button variant="secondary" size="small" onClick={openCombobox}>
                  <Icon type="pencil" size="small" mr={1} />
                  Indicators
                </Button>
                <Button variant="secondary" size="small" onClick={handleDeleteIndicators}>
                  <Icon type="trash" size="small" mr={1} />
                  Indicators
                </Button>
              </Flex>
            )}

            {/* Read-only indicator display — when no callback (non-editing contexts) */}
            {!onIndicatorsChange && field.indicators?.length > 0 && (
              <IndicatorList
                indicators={field.indicators}
                color={theme.palette.tokens.content.text.accent[3].base}
              />
            )}
          </Flex>
        </Flex>

        {field.description && (
          <Text
            ml={6}
            mt={1}
            textAlign="left"
            color={theme.palette.tokens.content.text.neutral[3]}
            fontStyle="italic"
          >
            {field.description}
          </Text>
        )}
        {field.isDiscovered && (
          <Text
            ml={6}
            mt={1}
            textAlign="left"
            color={theme.palette.tokens.content.text.semantic.suggest.base}
            fontStyle="italic"
          >
            <Icon type="thunder" size="small" mr={1} />
            Discovered field
          </Text>
        )}
      </Box>

      {isArray && field.element.type === 'object' && (
        <Box
          ml={6}
          borderLeft="1px solid"
          borderColor={theme.palette.tokens.stroke.neutral[3]}
          borderOpacity={0.15}
        >
          <SchemaFieldV3
            field={field.element}
            initialExpansionLevel={initialExpansionLevel}
            onIndicatorsChange={onIndicatorsChange}
          />
        </Box>
      )}

      {showNestedFields && (
        <Box
          as="ul"
          role="group"
          position="relative"
          ml={6}
          borderLeft="1px solid"
          borderColor={theme.palette.tokens.stroke.neutral[3]}
          borderOpacity={0.15}
        >
          {field.fields.map(childField => (
            <SchemaFieldV3
              key={childField.name}
              prefix={prefix ? `${prefix}${field.name ? `.${field.name}` : ''}` : field.name}
              field={childField}
              initialExpansionLevel={initialExpansionLevel > 0 ? initialExpansionLevel - 1 : 0}
              onIndicatorsChange={onIndicatorsChange}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default SchemaFieldV3;
